import React, { Fragment, useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import { showToast } from "../utils/library";

const API_BASE = WEBSERVICE;
const DEFAULT_BASE_URL = `${window.location.origin}/merchant/checkout/`;
const PAGE_SIZE = 10;

const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getBrokerId = () =>
  localStorage.getItem(SITENAMEALIAS + "_broker_id") || "";
const getToken = () => getAuthToken() || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const apiGet = (url) =>
  fetch(url, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error("Request failed: " + r.status);
    return r.json();
  });

const buildCheckoutBase = (domain) => {
  if (domain && typeof domain === "string" && domain.trim() !== "") {
    const d = domain.endsWith("/") ? domain : `${domain}/`;
    return `${d}payments/merchant/checkout/`;
  }
  return DEFAULT_BASE_URL;
};

const buildPaymentLink = (paymentId, baseUrl) => {
  const base = String(baseUrl || "").replace(/\/+$/, "");
  return `${base}/${encodeURIComponent(String(paymentId))}`;
};

const formatDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const pick = (obj, keys) => {
  if (!obj) return null;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return null;
};

const feeHandlingLabel = (v) => {
  if (v === 1 || v === "1") return "Customer Pays";
  if (v === 0 || v === "0") return "Merchant Pays";
  return "—";
};

const styles = {
  pageWrapper: { padding: "36px 24px 60px" },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "28px",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: "13px",
    color: "var(--text-color)",
    opacity: 0.5,
    margin: "4px 0 0",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color-less)",
    color: "var(--text-color)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 14,
  },
  card: {
    position: "relative",
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 12,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
    overflow: "hidden",
  },
  cardAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 3,
    height: "100%",
    background: "var(--btn-color)",
    opacity: 0.7,
  },
  cardTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  idChip: {
    fontFamily: "monospace",
    fontSize: 11,
    padding: "3px 8px",
    background: "color-mix(in srgb, var(--btn-color) 14%, transparent)",
    color: "var(--btn-color)",
    borderRadius: 5,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  cardDate: {
    fontSize: 11,
    color: "var(--text-color)",
    opacity: 0.5,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  cardName: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-color)",
    margin: "2px 0 4px",
    wordBreak: "break-word",
    lineHeight: 1.35,
    opacity: 0.85,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardNameEmpty: {
    fontSize: 12.5,
    fontWeight: 500,
    color: "var(--text-color)",
    opacity: 0.4,
    fontStyle: "italic",
    margin: "2px 0 4px",
  },
  viewBtn: {
    marginTop: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 7,
    border: "1px solid color-mix(in srgb, var(--btn-color) 40%, transparent)",
    background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
    color: "var(--btn-color)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all .15s",
    alignSelf: "flex-start",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  pageInfo: {
    fontSize: 13,
    color: "var(--text-color)",
    opacity: 0.6,
  },
  pageBtns: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  pageBtn: (active, disabled) => ({
    minWidth: 34,
    height: 34,
    padding: "0 10px",
    borderRadius: 8,
    border: active ? "none" : "1px solid var(--body-color-less2)",
    background: active ? "var(--btn-color)" : "transparent",
    color: active ? "#fff" : "var(--text-color)",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
    color: "var(--text-color)",
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 14,
  },
  emptyIcon: { fontSize: 48, opacity: 0.15, marginBottom: 16 },
  emptyText: { fontSize: 14, opacity: 0.4, fontWeight: 500 },
  loadingBox: {
    padding: "60px 20px",
    textAlign: "center",
    color: "var(--text-color)",
    opacity: 0.6,
    fontSize: 14,
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
    padding: 20,
  },
  modalCard: {
    background: "var(--body-color)",
    borderRadius: 16,
    width: 720,
    maxWidth: "100%",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    border: "1px solid var(--body-color-less2)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid var(--body-color-less2)",
    gap: 12,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "var(--text-color)",
    margin: 0,
  },
  modalSub: {
    fontSize: 12,
    color: "var(--text-color)",
    opacity: 0.55,
    marginTop: 3,
  },
  modalClose: {
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    border: "none",
    background: "var(--body-color-less2)",
    cursor: "pointer",
    fontSize: 18,
    color: "var(--text-color)",
  },
  modalBody: {
    padding: "18px 22px 24px",
    overflowY: "auto",
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-color)",
    opacity: 0.65,
    marginBottom: 8,
  },
  sectionCard: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 10,
    padding: "12px 14px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    padding: "6px 0",
    fontSize: 13,
    color: "var(--text-color)",
  },
  rowKey: { opacity: 0.55 },
  rowVal: { fontWeight: 600, textAlign: "right", wordBreak: "break-word" },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  pill: {
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    background: "rgba(99,102,241,.12)",
    color: "var(--text-color)",
  },
  productRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  },
  productImg: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: "cover",
    background: "var(--body-color-less2)",
    flexShrink: 0,
  },
  productName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text-color)",
    margin: 0,
  },
  productDesc: {
    fontSize: 12,
    opacity: 0.6,
    margin: "2px 0 0",
  },
  linkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid var(--body-color-less2)",
    background: "transparent",
    color: "var(--btn-color)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
  },
  dashed: { opacity: 0.45 },
};

function SectionTitle({ icon, label }) {
  return (
    <div style={styles.sectionTitle}>
      <i className={`mdi ${icon}`} style={{ fontSize: 14 }} />
      {label}
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowKey}>{k}</span>
      <span style={styles.rowVal}>
        {v !== null && v !== undefined && v !== "" ? (
          v
        ) : (
          <span style={styles.dashed}>—</span>
        )}
      </span>
    </div>
  );
}

function ProfileEmpty({ label }) {
  return (
    <div style={{ ...styles.sectionCard, fontSize: 13, opacity: 0.5 }}>
      No {label} selected
    </div>
  );
}

function DetailsModal({ open, onClose, loading, data, summary, link }) {
  if (!open) return null;

  const products = data?.products || [];
  const buyer = data?.buyersProfiles?.[0];
  const discount = data?.discounts?.[0];
  const billing = data?.billingProfiles?.[0];
  const shipping = data?.shippingProfiles?.[0];
  const redirect = data?.redirectUrlProfile?.[0];
  const feeLabel =
    data && data.isProcessingFeeApplied !== undefined
      ? feeHandlingLabel(data.isProcessingFeeApplied)
      : "—";

  const collectFields = (b) => {
    if (!b) return [];
    const map = [
      ["collectFullName", "Full Name"],
      ["collectEmail", "Email"],
      ["collectAddress", "Address"],
      ["collectPhoneNumber", "Phone Number"],
      ["collectCompanyName", "Company Name"],
      ["collectTaxInfo", "Tax Info"],
      ["collectCryptoRefundAddress", "Crypto Refund Address"],
      ["collectOrderNotes", "Order Notes"],
    ];
    return map.filter(([k]) => b[k] === 1 || b[k] === true).map(([, l]) => l);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <h3 style={styles.modalTitle}>
              {summary?.name || "Payment Details"}
            </h3>
            <div style={styles.modalSub}>
              <span style={{ fontFamily: "monospace" }}>{summary?.id}</span>
              {summary?.created ? ` • ${formatDate(summary.created)}` : ""}
            </div>
          </div>
          <button
            type="button"
            style={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            <i className="mdi mdi-close" />
          </button>
        </div>

        <div style={styles.modalBody}>
          {loading ? (
            <div style={styles.loadingBox}>
              <i
                className="mdi mdi-loading mdi-spin"
                style={{ fontSize: 22, marginRight: 8 }}
              />
              Loading details…
            </div>
          ) : !data ? (
            <div style={styles.loadingBox}>Unable to load details.</div>
          ) : (
            <>
              {link && (
                <div style={{ ...styles.section, marginBottom: 18 }}>
                  <SectionTitle icon="mdi-link-variant" label="Payment Link" />
                  <div
                    style={{
                      ...styles.sectionCard,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <code
                      style={{
                        fontSize: 12,
                        opacity: 0.75,
                        wordBreak: "break-all",
                        color: "var(--text-color)",
                      }}
                    >
                      {link}
                    </code>
                    <div style={{ display: "flex", gap: 8 }}>
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.linkBtn}
                      >
                        <i className="mdi mdi-open-in-new" /> Open
                      </a>
                      <button
                        type="button"
                        style={styles.linkBtn}
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(link);
                            showToast("success", "Link copied");
                          } catch (_) {
                            showToast("error", "Unable to copy");
                          }
                        }}
                      >
                        <i className="mdi mdi-content-copy" /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.section}>
                <SectionTitle icon="mdi-package-variant" label="Products" />
                {products.length === 0 ? (
                  <ProfileEmpty label="product" />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {products.map((p, i) => (
                      <div key={p.productId || i} style={styles.sectionCard}>
                        <div style={styles.productRow}>
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name}
                              style={styles.productImg}
                              onError={(e) => {
                                e.target.style.visibility = "hidden";
                              }}
                            />
                          ) : (
                            <div style={styles.productImg} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={styles.productName}>{p.name || "—"}</p>
                            {p.description && (
                              <p style={styles.productDesc}>{p.description}</p>
                            )}
                            {p.prices && p.prices[0] && (
                              <div style={styles.pillRow}>
                                {(p.prices[0].currencies || []).map((c, ci) => (
                                  <span
                                    key={ci}
                                    style={{
                                      ...styles.pill,
                                      background: c.default
                                        ? "color-mix(in srgb, var(--btn-color) 20%, transparent)"
                                        : styles.pill.background,
                                    }}
                                  >
                                    {c.currency} {c.amount}
                                    {c.default ? " ★" : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-credit-card-outline" label="Payment Option" />
                {billing ? (
                  <div style={styles.sectionCard}>
                    <InfoRow k="Profile" v={billing.profileName} />
                    <InfoRow k="Type" v={billing.billingType} />
                    {(billing.billingMethod || []).length > 0 && (
                      <div style={{ paddingTop: 6 }}>
                        <div style={{ ...styles.rowKey, fontSize: 13 }}>
                          Methods
                        </div>
                        <div style={styles.pillRow}>
                          {billing.billingMethod.map((m, i) => (
                            <span key={i} style={styles.pill}>
                              {m.methodName}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(billing.currencies || []).length > 0 && (
                      <div style={{ paddingTop: 6 }}>
                        <div style={{ ...styles.rowKey, fontSize: 13 }}>
                          Currencies
                        </div>
                        <div style={styles.pillRow}>
                          {billing.currencies.map((c, i) => (
                            <span key={i} style={styles.pill}>
                              {c.currency}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <ProfileEmpty label="payment option" />
                )}
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-cash-multiple" label="Fee Handling" />
                <div style={styles.sectionCard}>
                  <InfoRow k="Handling" v={feeLabel} />
                </div>
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-account-outline" label="Buyer Info" />
                {buyer ? (
                  <div style={styles.sectionCard}>
                    <InfoRow k="Profile" v={buyer.profileName} />
                    {(() => {
                      const fields = collectFields(buyer);
                      return fields.length > 0 ? (
                        <div style={{ paddingTop: 6 }}>
                          <div style={{ ...styles.rowKey, fontSize: 13 }}>
                            Collects
                          </div>
                          <div style={styles.pillRow}>
                            {fields.map((f, i) => (
                              <span key={i} style={styles.pill}>
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {(buyer.customFields || []).length > 0 && (
                      <div style={{ paddingTop: 6 }}>
                        <div style={{ ...styles.rowKey, fontSize: 13 }}>
                          Custom Fields
                        </div>
                        <div style={styles.pillRow}>
                          {buyer.customFields.map((f, i) => (
                            <span key={i} style={styles.pill}>
                              {f.fieldLabel}
                              {f.fieldType ? ` (${f.fieldType})` : ""}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <ProfileEmpty label="buyer info" />
                )}
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-truck-outline" label="Shipping" />
                {shipping ? (
                  <div style={styles.sectionCard}>
                    <InfoRow k="Profile" v={shipping.profileName} />
                    <InfoRow
                      k="Handling Fee"
                      v={shipping.handlingFeeValue}
                    />
                    <InfoRow k="Tax Rate" v={shipping.taxRate} />
                  </div>
                ) : (
                  <ProfileEmpty label="shipping" />
                )}
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-percent-outline" label="Discount" />
                {discount ? (
                  <div style={styles.sectionCard}>
                    <InfoRow k="Profile" v={discount.profileName} />
                    <InfoRow
                      k="Discount"
                      v={
                        discount.discountPercentage !== undefined
                          ? `${discount.discountPercentage}%`
                          : null
                      }
                    />
                    <InfoRow
                      k="Min. Cart Value"
                      v={discount.minimumCartValue}
                    />
                  </div>
                ) : (
                  <ProfileEmpty label="discount" />
                )}
              </div>

              <div style={styles.section}>
                <SectionTitle icon="mdi-arrow-right-bold-outline" label="Redirects" />
                {redirect ? (
                  <div style={styles.sectionCard}>
                    <InfoRow k="Template" v={redirect.templateName} />
                    <InfoRow k="Success URL" v={redirect.successUrl} />
                    <InfoRow k="Failure URL" v={redirect.failureUrl} />
                  </div>
                ) : (
                  <ProfileEmpty label="redirects" />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ViewPayments() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkoutBase, setCheckoutBase] = useState(DEFAULT_BASE_URL);
  const [page, setPage] = useState(1);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsSummary, setDetailsSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const brokerId = getBrokerId();
        const infoRes = await fetch(
          `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${encodeURIComponent(
            brokerId
          )}`
        ).then((r) => r.json());
        setCheckoutBase(buildCheckoutBase(infoRes && infoRes.domain));
      } catch (_) {
        setCheckoutBase(DEFAULT_BASE_URL);
      }
    })();
  }, []);

  useEffect(() => {
    const mid = getMerchantId();
    if (!mid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await apiGet(
          `${API_BASE}/payment/payment-details?merchantId=${encodeURIComponent(
            mid
          )}&offset=${page}&limit=${PAGE_SIZE}`
        );
        if (cancelled) return;
        setRows(Array.isArray(res?.payments) ? res.payments : []);
        setTotalCount(
          typeof res?.totalCount === "number" ? res.totalCount : 0
        );
      } catch (e) {
        if (!cancelled) {
          showToast("error", "Failed to load payment links");
          setRows([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const pageItems = useMemo(() => {
    return rows.map((r, i) => {
      const id = pick(r, [
        "paymentId",
        "payment_id",
        "id",
        "paymentOrderId",
        "payment_order_id",
      ]);
      const name = pick(r, ["paymentName", "payment_name", "name"]);
      const created = pick(r, [
        "createdAt",
        "created_at",
        "createdTime",
        "created_time",
        "createdOn",
        "created_on",
        "creationTime",
      ]);
      return {
        key: id || `row-${i}`,
        id,
        name,
        created,
      };
    });
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;

  const handleView = async (item) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsData(null);
    setDetailsSummary(item);
    try {
      const mid = getMerchantId();
      const res = await apiGet(
        `${API_BASE}/payment/profileDetailsById?id=${encodeURIComponent(
          item.id
        )}&merchantId=${encodeURIComponent(mid)}`
      );
      if (res && res.error !== undefined && res.error !== 0 && res.error !== "0") {
        showToast("error", res.message || "Failed to fetch details");
        setDetailsData(null);
      } else {
        setDetailsData(res);
      }
    } catch (e) {
      showToast("error", "Failed to fetch details");
      setDetailsData(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const half = Math.floor(max / 2);
    let s = Math.max(1, currentPage - half);
    let e = Math.min(totalPages, s + max - 1);
    s = Math.max(1, e - max + 1);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }, [currentPage, totalPages]);

  return (
    <Fragment>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={styles.pageWrapper}>
            <div style={styles.headerRow}>
              <div>
                <h2 style={styles.pageTitle}>Payments</h2>
                <p style={styles.pageSubtitle}>
                  All payment links you've created
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  window.location.href = "/create-payment";
                }}
                style={styles.backBtn}
              >
                <i className="mdi mdi-arrow-left" style={{ fontSize: 16 }} />
                Back to Create Payment
              </button>
            </div>

            {loading ? (
              <div style={{ ...styles.emptyState }}>
                <i
                  className="mdi mdi-loading mdi-spin"
                  style={{ fontSize: 22, marginRight: 8 }}
                />
                Loading payment links…
              </div>
            ) : totalCount === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <i className="mdi mdi-link-variant-off" />
                </div>
                <div style={styles.emptyText}>
                  No payment links created yet
                </div>
              </div>
            ) : (
              <>
                <div style={styles.grid}>
                  {pageItems.map((item) => (
                    <div key={item.key} style={styles.card}>
                      <span style={styles.cardAccent} />
                      <div style={styles.cardTopRow}>
                        <span style={styles.idChip}>{item.id || "—"}</span>
                        <span style={styles.cardDate}>
                          <i
                            className="mdi mdi-calendar-blank-outline"
                            style={{ fontSize: 12 }}
                          />
                          {formatDate(item.created)}
                        </span>
                      </div>
                      {item.name ? (
                        <div style={styles.cardName} title={item.name}>
                          {item.name}
                        </div>
                      ) : (
                        <div style={styles.cardNameEmpty}>No Name</div>
                      )}
                      <button
                        type="button"
                        style={styles.viewBtn}
                        onClick={() => handleView(item)}
                        disabled={!item.id}
                      >
                        <i
                          className="mdi mdi-eye-outline"
                          style={{ fontSize: 14 }}
                        />
                        View
                      </button>
                    </div>
                  ))}
                </div>

                <div style={styles.paginationBar}>
                  <div style={styles.pageInfo}>
                    Showing {start + 1}–
                    {Math.min(start + pageItems.length, totalCount)} of{" "}
                    {totalCount}
                  </div>
                  <div style={styles.pageBtns}>
                    <button
                      type="button"
                      style={styles.pageBtn(false, currentPage === 1)}
                      onClick={() => setPage(1)}
                      disabled={currentPage === 1}
                      aria-label="First page"
                    >
                      <i className="mdi mdi-chevron-double-left" />
                    </button>
                    <button
                      type="button"
                      style={styles.pageBtn(false, currentPage === 1)}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <i className="mdi mdi-chevron-left" />
                    </button>
                    {pageNumbers.map((n) => (
                      <button
                        key={n}
                        type="button"
                        style={styles.pageBtn(n === currentPage, false)}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      type="button"
                      style={styles.pageBtn(false, currentPage === totalPages)}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <i className="mdi mdi-chevron-right" />
                    </button>
                    <button
                      type="button"
                      style={styles.pageBtn(false, currentPage === totalPages)}
                      onClick={() => setPage(totalPages)}
                      disabled={currentPage === totalPages}
                      aria-label="Last page"
                    >
                      <i className="mdi mdi-chevron-double-right" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Footer />
        </div>
      </div>

      <DetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        loading={detailsLoading}
        data={detailsData}
        summary={detailsSummary}
        link={
          detailsSummary?.id
            ? buildPaymentLink(detailsSummary.id, checkoutBase)
            : ""
        }
      />
    </Fragment>
  );
}
